import { TrackingEventName } from '@/models/analytics/eventTypes';
import {
  type SessionMergeConfirmedEventProps,
  type SessionMergeDissolveCancelledEventProps,
  type SessionMergeDissolveConfirmedEventProps,
  type SessionMergeDissolvePromptViewedEventProps,
  type SessionMergeFailedEventProps,
  type SessionMergeIgnoredEventProps,
  type SessionMergeOnboardingAcceptedEventProps,
  type SessionMergeOnboardingDismissedEventProps,
  type SessionMergeOnboardingViewedEventProps,
  type SessionMergeRelinkedEventProps,
  type SessionMergeReviewDismissedEventProps,
  type SessionMergeReviewViewedEventProps,
  type SessionMergeTabViewedEventProps,
  type SessionMergeUnlinkCancelledEventProps,
  type SessionMergeUnlinkConfirmedEventProps,
  type SessionMergeUnlinkedEventProps,
  type SessionMergeUnlinkPromptViewedEventProps,
} from '@heidi/session/contracts';
import { trackSelfDescribingEvent } from '@snowplow/browser-tracker';
import type { SnowplowEventMap } from '../trackSnowplowEvent';

// Hand-rolled self-describing calls — the Iglu schemas are published
// (data-structures/com.heidi/session_merge_*.yaml at 1-0-0) but the checked-in
// Snowtype output can't be regenerated here (the console API credentials live
// in 1Password / CI). TODO(snowtype): once snowtype/snowplow.ts is regenerated
// with console creds, swap these to the generated trackSessionMerge*Spec
// functions. Mirrors the patient_summary_email_* fallback in `patients.ts`.
// The URIs below must match the schema `self.name` + `version` exactly.
const SESSION_MERGE_SCHEMA_VERSION = '1-0-0';
const schema = (name: string) =>
  `iglu:com.heidi/${name}/jsonschema/${SESSION_MERGE_SCHEMA_VERSION}`;

export const sessionMergeEvents: SnowplowEventMap = {
  [TrackingEventName.SessionMergeReviewViewed]: (props) =>
    trackSelfDescribingEvent({
      event: {
        schema: schema('session_merge_review_viewed'),
        data: props as SessionMergeReviewViewedEventProps,
      },
    }),
  [TrackingEventName.SessionMergeConfirmed]: (props) =>
    trackSelfDescribingEvent({
      event: {
        schema: schema('session_merge_confirmed'),
        data: props as SessionMergeConfirmedEventProps,
      },
    }),
  [TrackingEventName.SessionMergeIgnored]: (props) =>
    trackSelfDescribingEvent({
      event: {
        schema: schema('session_merge_ignored'),
        data: props as SessionMergeIgnoredEventProps,
      },
    }),
  [TrackingEventName.SessionMergeReviewDismissed]: (props) =>
    trackSelfDescribingEvent({
      event: {
        schema: schema('session_merge_review_dismissed'),
        data: props as SessionMergeReviewDismissedEventProps,
      },
    }),
  [TrackingEventName.SessionMergeFailed]: (props) =>
    trackSelfDescribingEvent({
      event: {
        schema: schema('session_merge_failed'),
        data: props as SessionMergeFailedEventProps,
      },
    }),
  [TrackingEventName.SessionMergeTabViewed]: (props) =>
    trackSelfDescribingEvent({
      event: {
        schema: schema('session_merge_tab_viewed'),
        data: props as SessionMergeTabViewedEventProps,
      },
    }),
  [TrackingEventName.SessionMergeUnlinked]: (props) =>
    trackSelfDescribingEvent({
      event: {
        schema: schema('session_merge_unlinked'),
        data: props as SessionMergeUnlinkedEventProps,
      },
    }),
  [TrackingEventName.SessionMergeRelinked]: (props) =>
    trackSelfDescribingEvent({
      event: {
        schema: schema('session_merge_relinked'),
        data: props as SessionMergeRelinkedEventProps,
      },
    }),
  [TrackingEventName.SessionMergeDissolvePromptViewed]: (props) =>
    trackSelfDescribingEvent({
      event: {
        schema: schema('session_merge_end_prompt_viewed'),
        data: props as SessionMergeDissolvePromptViewedEventProps,
      },
    }),
  [TrackingEventName.SessionMergeDissolveConfirmed]: (props) =>
    trackSelfDescribingEvent({
      event: {
        schema: schema('session_merge_end_confirmed'),
        data: props as SessionMergeDissolveConfirmedEventProps,
      },
    }),
  [TrackingEventName.SessionMergeDissolveCancelled]: (props) =>
    trackSelfDescribingEvent({
      event: {
        schema: schema('session_merge_end_cancelled'),
        data: props as SessionMergeDissolveCancelledEventProps,
      },
    }),
  [TrackingEventName.SessionMergeUnlinkPromptViewed]: (props) =>
    trackSelfDescribingEvent({
      event: {
        schema: schema('session_merge_unlink_prompt_viewed'),
        data: props as SessionMergeUnlinkPromptViewedEventProps,
      },
    }),
  [TrackingEventName.SessionMergeUnlinkConfirmed]: (props) =>
    trackSelfDescribingEvent({
      event: {
        schema: schema('session_merge_unlink_confirmed'),
        data: props as SessionMergeUnlinkConfirmedEventProps,
      },
    }),
  [TrackingEventName.SessionMergeUnlinkCancelled]: (props) =>
    trackSelfDescribingEvent({
      event: {
        schema: schema('session_merge_unlink_cancelled'),
        data: props as SessionMergeUnlinkCancelledEventProps,
      },
    }),
  [TrackingEventName.SessionMergeOnboardingViewed]: (props) =>
    trackSelfDescribingEvent({
      event: {
        schema: schema('session_merge_onboarding_viewed'),
        data: props as SessionMergeOnboardingViewedEventProps,
      },
    }),
  [TrackingEventName.SessionMergeOnboardingAccepted]: (props) =>
    trackSelfDescribingEvent({
      event: {
        schema: schema('session_merge_onboarding_accepted'),
        data: props as SessionMergeOnboardingAcceptedEventProps,
      },
    }),
  [TrackingEventName.SessionMergeOnboardingDismissed]: (props) =>
    trackSelfDescribingEvent({
      event: {
        schema: schema('session_merge_onboarding_dismissed'),
        data: props as SessionMergeOnboardingDismissedEventProps,
      },
    }),
};
